/**
 * What a template author got wrong, in words that name it.
 *
 * @remarks
 * These are the mistakes that are knowable without fetching anything: a key
 * that is missing, a value in the wrong shape, a filter nothing can satisfy.
 * They are reported rather than thrown, because throwing during render tears
 * down the whole form: the filler gets a blank page and nothing says which
 * template is wrong.
 *
 * Where the message lands is a separate question, and each picker family
 * answers it for itself: it becomes a failure the form shows, always fatal
 * because a template does not repair itself. This module only produces the
 * words.
 *
 * @packageDocumentation
 */

/**
 * A required `ui:options` key that is absent.
 *
 * @param key - The key the author left out.
 * @param why - What it is for, so the message says more than "it is missing".
 */
export function missingOption(key: string, why: string): string {
  return `This field needs "${key}" in its ui:options: ${why}.`;
}

/**
 * An option written in the wrong shape.
 *
 * @remarks
 * Saying which shape is wanted beats "invalid", which leaves the author
 * guessing what he should have written instead.
 *
 * @param key - The option written wrongly.
 * @param wanted - The shape it takes, phrased to drop into the message.
 */
export function wrongShape(key: string, wanted: string): string {
  return `"${key}" takes ${wanted}.`;
}

/**
 * A value outside the word list a field has.
 *
 * @remarks
 * A value outside a fixed vocabulary can never match, so saying so beats an
 * empty list the author has to explain to himself. The valid ones come along,
 * because a message that only says "no" makes him go look them up.
 *
 * @param field - What the value was meant to name.
 * @param written - What the author wrote.
 * @param valid - The values that do exist.
 */
export function unknownValue(
  field: string,
  written: string,
  valid: readonly string[],
): string {
  return `"${written}" is not a ${field}. The ones that exist are: ${valid.join(
    ', ',
  )}.`;
}

/**
 * A `filter` key the picker does not offer.
 *
 * @remarks
 * The test is whether THIS picker offers it, not whether the upstream knows it:
 * a picker may offer a filter the backend has no field for, and may withhold
 * one it does have.
 *
 * @param written - The key the author filtered on.
 * @param offered - The keys this picker does offer.
 */
export function unknownFilterField(
  written: string,
  offered: readonly string[],
): string {
  return `This picker cannot filter on "${written}". It filters on: ${offered.join(
    ', ',
  )}.`;
}
